/**
 * PageHeader - standardized page header with icon, title, subtitle and optional actions
 */
import React from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/utils/cn';
import { tokens } from '@/theme/tokens';
import { Button } from '../Button';
import { Heading, Text } from '../Typography';

interface HeaderAction {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  variant?: 'primary' | 'success' | 'danger' | 'warning' | 'secondary' | 'ghost';
  disabled?: boolean;
  loading?: boolean;
}

interface PageHeaderProps {
  icon?: LucideIcon;
  iconColor?: string;
  title: string;
  subtitle?: string;
  rightContent?: React.ReactNode;
  actions?: HeaderAction[];
  className?: string;
  onBack?: () => void;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  icon: Icon,
  iconColor = 'text-green-500',
  title,
  subtitle,
  rightContent,
  actions,
  className,
  onBack,
}) => {
  const accent = getAccentPalette(iconColor);

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-[28px] p-6 sm:p-7 lg:p-8 mb-6',
        tokens.surface.hero,
        className,
      )}
    >
      <div className={cn('absolute inset-x-0 top-0 h-1 bg-gradient-to-r', accent.ribbon)} />
      <div className={cn('absolute -right-12 top-0 h-32 w-32 rounded-full blur-3xl', accent.orb)} />

      <div className="relative flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-4 min-w-0">
          {onBack && (
            <button
              onClick={onBack}
              aria-label="Zpět"
              title="Zpět"
              className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-emerald-100/80 bg-white/80 text-gray-600 hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-zinc-200 dark:hover:bg-white/10 transition-colors"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-arrow-left"
              >
                <path d="m12 19-7-7 7-7" />
                <path d="M19 12H5" />
              </svg>
            </button>
          )}
          {Icon && (
            <div
              className={cn(
                'relative flex h-14 w-14 shrink-0 items-center justify-center rounded-[22px] border shadow-sm',
                accent.iconSurface,
              )}
            >
              <Icon size={26} className={cn('relative z-10', accent.iconText)} />
            </div>
          )}
          <div className="min-w-0">
            <Heading variant="h1" className="sm:text-[2rem]" color="text-slate-900 dark:text-white">
              {title}
            </Heading>
            {subtitle && (
              <Text
                variant="body-sm"
                as="p"
                className="mt-2 max-w-3xl leading-6"
                color="text-slate-600 dark:text-zinc-300"
              >
                {subtitle}
              </Text>
            )}
          </div>
        </div>

        {(rightContent || actions) && (
          <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-white/70 bg-white/70 p-2 dark:border-white/10 dark:bg-white/5 lg:justify-end">
            {rightContent}
            {actions?.map((action, i) => (
              <Button
                key={i}
                variant={action.variant || 'primary'}
                size="md"
                leftIcon={action.icon}
                onClick={action.onClick}
                disabled={action.disabled}
                loading={action.loading}
              >
                <span className="hidden sm:inline">{action.label}</span>
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const getAccentPalette = (iconColor: string) => {
  const tone = iconColor.toLowerCase();

  if (tone.includes('red')) {
    return {
      ribbon: 'from-rose-400 via-red-400 to-orange-400',
      orb: 'bg-rose-300/30 dark:bg-rose-500/20',
      iconSurface: 'bg-rose-50/90 border-rose-100 dark:bg-rose-500/10 dark:border-rose-400/20',
      iconText: 'text-rose-600 dark:text-rose-300',
    };
  }

  if (tone.includes('yellow') || tone.includes('amber') || tone.includes('orange')) {
    return {
      ribbon: 'from-amber-300 via-orange-300 to-yellow-300',
      orb: 'bg-amber-300/30 dark:bg-amber-500/20',
      iconSurface: 'bg-amber-50/90 border-amber-100 dark:bg-amber-500/10 dark:border-amber-400/20',
      iconText: 'text-amber-600 dark:text-amber-300',
    };
  }

  if (tone.includes('cyan') || tone.includes('sky')) {
    return {
      ribbon: 'from-emerald-300 via-teal-300 to-cyan-300',
      orb: 'bg-cyan-300/30 dark:bg-cyan-500/20',
      iconSurface: 'bg-cyan-50/90 border-cyan-100 dark:bg-cyan-500/10 dark:border-cyan-400/20',
      iconText: 'text-cyan-600 dark:text-cyan-300',
    };
  }

  if (tone.includes('teal')) {
    return {
      ribbon: 'from-emerald-300 via-teal-300 to-cyan-300',
      orb: 'bg-teal-300/30 dark:bg-teal-500/20',
      iconSurface: 'bg-teal-50/90 border-teal-100 dark:bg-teal-500/10 dark:border-teal-400/20',
      iconText: 'text-teal-600 dark:text-teal-300',
    };
  }

  return {
    ribbon: 'from-emerald-300 via-teal-300 to-cyan-300',
    orb: 'bg-emerald-300/30 dark:bg-emerald-500/20',
    iconSurface: 'bg-emerald-50/90 border-emerald-100 dark:bg-emerald-500/10 dark:border-emerald-400/20',
    iconText: 'text-emerald-600 dark:text-emerald-300',
  };
};
